// 📄 types/miEspacio.ts
// Formas de datos del módulo personal "Mi Espacio" (snippets, notas de SAP,
// pendientes y contactos). Cada colección vive bajo usuarios/{uid}/... (si
// es "Global/Freelance") o usuarios/{uid}/projectData/{projectId}/... (si
// está asociada a un proyecto) — ver miEspacioService.ts y firestore.rules.

export interface SnippetData {
  titulo: string;
  lenguaje: string;
  tags: string[];
  codigo: string;
  descripcion: string;
  // Opcional: dónde vive este código dentro del sistema SAP — BAdI,
  // programa/include, User Exit, etc. Texto libre (no referencia al
  // Catálogo de Programas/BAdIs — esto es una nota personal de "Mi
  // Espacio", no un dato compartido). Agregado después: snippets
  // existentes no lo tienen, se trata como '' en toda la UI.
  ubicacionTecnica?: string;
}

// `projectId` no se guarda en el documento de Firestore — lo agrega
// miEspacioService.ts a partir de qué ruta se consultó (null = espacio
// Global/Freelance), para que cada tarjeta/acción sepa a qué colección
// real pertenece sin tener que volver a pasarlo por fuera.
export interface Snippet extends SnippetData {
  id: string;
  projectId: string | null;
  createdAt?: any;
  updatedAt?: any;
}

export interface NotaSapData {
  titulo: string; // Título / nombre del error
  numeroONota: string; // Número (ej. 214285) o URL completa de la Nota SAP — opcional
  modulo: string;
  causa: string;
  solucion: string;
  // Campos opcionales agregados después — registros existentes no los
  // tienen (undefined), tratarlos como "sin dato" en toda la UI.
  tipoNota?: string;
  sistemaAfectado?: string;
  campoTecnico?: string;
  tags?: string[];
  versionAmbiente?: string;
  fechaVerificacion?: Date | null;
  // Solo tiene efecto cuando la nota se guarda con un projectId (ver
  // NotasSapSection.tsx) — dispara el espejo en
  // projects/{projectId}/notasSapCompartidas (ver miEspacioCompartidoService.ts).
  compartirConProyecto?: boolean;
}

export interface NotaSap extends NotaSapData {
  id: string;
  projectId: string | null;
  createdAt?: any;
  updatedAt?: any;
}

// Copia de solo lectura de una NotaSap compartida por otro miembro del
// proyecto — vive en projects/{projectId}/notasSapCompartidas, no en
// usuarios/{uid}/... (ver miEspacioCompartidoService.ts). Se arma a mano
// (no extiende NotaSap) porque le faltan updatedAt/compartirConProyecto y
// le sobra autorUid/autorEmail, que NotaSap no tiene.
export interface NotaSapCompartida {
  id: string;
  autorUid: string;
  autorEmail: string;
  titulo: string;
  numeroONota: string;
  modulo: string;
  causa: string;
  solucion: string;
  tipoNota?: string;
  sistemaAfectado?: string;
  campoTecnico?: string;
  tags?: string[];
  versionAmbiente?: string;
  fechaVerificacion?: Date | null;
  createdAt?: any;
}

export interface TareaData {
  titulo: string;
  completada: boolean;
  // Opcionales agregados después — tareas existentes no los tienen.
  prioridad?: string;
  fechaLimite?: Date | null;
  categoria?: string;
  notas?: string;
}

export interface Tarea extends TareaData {
  id: string;
  projectId: string | null;
  createdAt?: any;
}

export interface ContactoData {
  nombreCompleto: string;
  empresa: string;
  rol: string;
  email: string;
  telefono: string;
  notas: string;
}

export interface Contacto extends ContactoData {
  id: string;
  projectId: string | null;
  createdAt?: any;
  updatedAt?: any;
}

// Módulos sugeridos para clasificar una nota de SAP — campo `modulo` sigue
// siendo texto libre, esto solo alimenta el <select>.
export const MODULOS_NOTA_SAP: string[] = [
  'Commerce',
  'Buying',
  'Sourcing',
  'SLP',
  'Basis',
  'BTP',
  'ERP',
  'General'
];

// Tipo de nota — primer campo del form, define el color del badge en la
// card (ver TIPO_NOTA_STYLES en NotasSapSection.tsx).
export const TIPOS_NOTA: string[] = [
  'Error resuelto',
  'Configuración',
  'Proceso',
  'Advertencia',
  'Referencia general'
];

export const SISTEMAS_AFECTADOS_NOTA: string[] = [
  'SAP Ariba',
  'ERP (ECC/R3)',
  'S/4HANA',
  'SAP BTP / CIG',
  'Otro'
];

export const PRIORIDADES_TAREA: string[] = ['Alta', 'Media', 'Baja'];

export const CATEGORIAS_TAREA: string[] = [
  'Seguimiento',
  'Revisión técnica',
  'Reunión / Sesión',
  'Entrega',
  'Otro'
];

// ── Archivos (2026-08-20) ────────────────────────────────────────────────
// A diferencia de las otras 4 pestañas, no es un formulario con campos
// propios — es un archivo subido a Storage (ver storageService.ts,
// UploadedAttachment) con un nombre/descripción editables por encima.
// Mismo path de colección (usuarios/{uid}/... o
// usuarios/{uid}/projectData/{projectId}/...) que el resto, ver
// miEspacioService.ts. El archivo en sí NUNCA se reemplaza en un edit —
// solo se puede renombrar/redescribir o borrar el registro entero (mismo
// criterio simple que el resto de "Mi Espacio", sin versión ni historial).
export interface ArchivoData {
  nombre: string;
  descripcion: string;
}

export interface Archivo extends ArchivoData {
  id: string;
  projectId: string | null;
  // `UploadedAttachment` (name/url/contentType/size) del propio
  // storageService.ts — se importa desde ahí en vez de duplicar el shape.
  archivo: { name: string; url: string; contentType: string; size: number };
  createdAt?: any;
}

// Extensiones aceptadas por el selector de archivo (accept del <input>) —
// PDF, Office (Word/Excel/PowerPoint, formato viejo y .x nuevo) y XML. Es
// solo una guía en el picker del sistema operativo, no una validación real
// (el usuario puede seleccionar "todos los archivos" e igual subir
// cualquier cosa) — firestore/storage.rules no restringen por tipo, mismo
// criterio que Casos de Prueba/Especificaciones Técnicas.
export const TIPOS_ARCHIVO_ACEPTADOS =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.xml,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/xml,application/xml';

// Sugeridos para el <select> de Snippets — texto libre históricamente, así
// que si un snippet existente tiene un valor que no está acá, se inyecta
// como opción extra en tiempo de render (ver SnippetsSection.tsx) en vez
// de perderlo.
export const LENGUAJES_SNIPPET: string[] = [
  'ABAP',
  'JavaScript',
  'JSON',
  'XML',
  'SQL',
  'Python',
  'Shell',
  'Otro'
];
